import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { API_BASE_URL } from "../config/apiBase";
import { buildCurriculumLessonMap, deriveTutorStudent } from "./tutorWorkflowData";
import { getAuthUserHeader } from "../utils/userRoles";

const API = API_BASE_URL;

function displayScore(score) {
  return score === null || score === undefined ? "—" : `${score}/100`;
}

export default function TutorStudentProgressPage() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [curriculum, setCurriculum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const [dashboardRes, curriculumRes] = await Promise.all([
          fetch(`${API}/tutor/dashboard`, {
            cache: "no-store",
            headers: { "X-User": getAuthUserHeader() },
          }),
          fetch(`${API}/curriculum`, { cache: "no-store" }),
        ]);
        const [dashboardJson, curriculumJson] = await Promise.all([
          dashboardRes.json().catch(() => null),
          curriculumRes.json().catch(() => null),
        ]);
        if (cancelled) return;
        if (!dashboardRes.ok) throw new Error(dashboardJson?.detail || "Unable to load student progress.");
        setStudents(Array.isArray(dashboardJson?.students) ? dashboardJson.students : []);
        setCurriculum(curriculumJson || null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Unable to load student progress.");
          setStudents([]);
          setCurriculum(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const curriculumMap = useMemo(() => buildCurriculumLessonMap(curriculum), [curriculum]);
  const rows = useMemo(
    () => students.map((student) => deriveTutorStudent(student, curriculumMap)),
    [students, curriculumMap]
  );

  return (
    <section style={styles.page}>
      <header style={styles.header}>
        <div>
          <div style={styles.eyebrow}>Student Progress</div>
          <h1 style={styles.title}>All enrolled students</h1>
          <div style={styles.subtitle}>
            {loading ? "Loading progress..." : `${rows.length} students`}
          </div>
        </div>
      </header>

      {error ? <div style={styles.error}>{error}</div> : null}

      <div style={styles.grid}>
        {rows.map((student) => (
          <article key={student.student_id} style={styles.card}>
            <div style={styles.cardTop}>
              <div>
                <div style={styles.cardTitle}>{student.name || student.student_id}</div>
                <div style={styles.cardMeta}>ID: {student.student_id}</div>
              </div>
              <div style={styles.statusPill}>{student.status_summary}</div>
            </div>

            <div style={styles.metaGrid}>
              <div>
                <div style={styles.metaLabel}>Completed</div>
                <div style={styles.metaValue}>
                  {student.completed_lessons}/{student.total_lessons || 16}
                </div>
              </div>
              <div>
                <div style={styles.metaLabel}>Current lesson</div>
                <div style={styles.metaValue}>{student.current_lesson_id || student.latest_lesson_id || "—"}</div>
              </div>
              <div>
                <div style={styles.metaLabel}>Last activity</div>
                <div style={styles.metaValue}>
                  {student.latest_activity ? new Date(student.latest_activity).toLocaleString() : "—"}
                </div>
              </div>
              <div>
                <div style={styles.metaLabel}>Average / best</div>
                <div style={styles.metaValue}>
                  {displayScore(student.average_score)} · {displayScore(student.best_score)}
                </div>
              </div>
            </div>

            <button
              type="button"
              style={styles.progressButton}
              onClick={() => navigate(`/tutor/student/${student.student_id}`)}
            >
              Open Progress
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}

const styles = {
  page: {
    padding: "24px 0 36px",
    display: "grid",
    gap: 18,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
  },
  eyebrow: {
    color: "#93c5fd",
    textTransform: "uppercase",
    letterSpacing: ".08em",
    fontSize: 12,
    fontWeight: 700,
  },
  title: {
    margin: "6px 0 0",
    color: "var(--text)",
  },
  subtitle: {
    marginTop: 6,
    color: "var(--muted)",
  },
  error: {
    color: "#fecaca",
    background: "rgba(127, 29, 29, .35)",
    border: "1px solid rgba(248, 113, 113, .35)",
    borderRadius: 12,
    padding: 14,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 14,
  },
  card: {
    background: "rgba(14,19,27,.92)",
    border: "1px solid #1a2230",
    borderRadius: 16,
    padding: 16,
    display: "grid",
    gap: 12,
  },
  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
  },
  cardTitle: {
    color: "var(--text)",
    fontSize: 18,
    fontWeight: 800,
  },
  cardMeta: {
    color: "var(--muted)",
    fontSize: 12,
    marginTop: 4,
  },
  statusPill: {
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(59,130,246,.14)",
    border: "1px solid rgba(59,130,246,.25)",
    color: "#dbeafe",
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  metaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },
  metaLabel: {
    color: "var(--muted)",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: ".08em",
    marginBottom: 4,
  },
  metaValue: {
    color: "var(--text)",
    fontWeight: 700,
    lineHeight: 1.45,
  },
  progressButton: {
    appearance: "none",
    border: "1px solid rgba(201,165,106,.55)",
    background: "rgba(201,165,106,.14)",
    color: "#f0e6d4",
    padding: "10px 14px",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 800,
  },
};
